import { defineStore } from 'pinia'

import {
    get,
    last,
    set,
    clone,
    has,
    isNil,
    each,
    dropRight,
} from 'lodash'

import { isCancel } from 'axios'

import linkType from '@/constants/linkType'
import { api, mock } from '@/services'
import { events } from '@/composables/events'
import { useBadgeStore } from '@/stores/badge'

// axios cancel handler
let controller = null

export const useModuleStore = defineStore('module', {
    state: () => {
        return {
            modules: {},
            loadingId: null,
            stack: [],
            readItemIdsByModuleUrl: {},
        }
    },
    getters: {
        fetching: (state) => state.loadingId !== null,
        moduleFromId: (state) => (id) => get(state.modules, id),
        currentModule: (state) => get(state.modules, last(state.stack)),
        mostRecentUrl: (state) => {
            const uuid = findLast(state.stack, (uuid) => {
                return get(state.modules, `${uuid}.link.type`) === linkType.apiComponent
            })
            return get(state.modules, `${uuid}.link.url`)
        },
        currentModulesReadItemIds: (state) => {
            return state.readItemIdsByModuleUrl[state.mostRecentUrl] || []
        },
    },
    actions: {
        loadContent(url, params = {}) {
            // cancel any current requests
            if (controller) {
                controller.abort()
            }

            controller = new AbortController()

            return api.get(url, {
                signal: controller.signal,
                params,
            })
        },
        replaceData(moduleId, data) {
            this.loadingId = moduleId
            set(this.modules, `${moduleId}.data`, data)
            delay(() => { this.loadingId = null }, 200)
        },
        refreshParent() {
            if (this.stack.length < 2) {
                // Don't do anything if there isn't a parent on the stack
                return
            }
            each(dropRight(this.stack, 2), (id) => {
                // Mark ancestor modules as stale
                this.modules[id].stale = true
            })
            // Load the previous module
            this.loadModule(this.stack[this.stack.length - 2])
        },
        async loadModule(id, withoutCache = false) {
            // Check if refresh_url was provided in content
            const url = this.modules[id]?.data?.meta?.refresh_url
                // Fallback to link url if refresh_url is not provided
                ?? this.modules[id]?.link?.url

            if (!url) {
                // No url to refresh
                return
            }

            // cancel any current requests
            if (controller) {
                controller.abort()
            }

            controller = new AbortController()

            // Clear module read item ids
            delete this.readItemIdsByModuleUrl[url]

            const params = this.modules[id]?.params ?? {}

            this.loadingId = id
            return api.get(url, {
                signal: controller.signal,
                params,
                headers: withoutCache ? { 'X-NO-CACHE': true } : {},
            })
                .then(({ data }) => {
                    set(this.modules, `${id}.data`, data)

                    this.loadingId = null
                })
                .catch((error) => {
                    if (!isCancel(error)) {
                        // Only disable loading if not cancelled
                        this.loadingId = null
                    }
                    throw error
                })
        },
        addModule(module) {
            if (!has(this.modules, module.id)) {
                set(this.modules, module.id, clone(module))
            }
            this.stack.push(module.id)
        },
        replaceLastModule(module) {
            this.stack.pop()
            this.addModule(module)
        },
        removeModules(moduleIds) {
            moduleIds.forEach((removedModuleId) => {
                const removedModule = get(this.modules, removedModuleId)
                // Clear read items when module has been removed
                if (removedModule?.link?.type === linkType.apiComponent) {
                    delete this.readItemIdsByModuleUrl[removedModule.link.url]
                }
                // Delete the module from the store
                if (removedModule?.link?.type === linkType.component) {
                    setTimeout(() => {
                        delete this.modules[removedModuleId]
                    }, 1000)
                }
            })
        },
        removeModulesUntil(nextModuleId) {
            // If next module in stack is stale, refresh it
            if (this.modules[nextModuleId]?.stale) {
                this.loadModule(nextModuleId)
                this.modules[nextModuleId].stale = false
            }

            const itemsToRemove = this.stack.length - this.stack.lastIndexOf(nextModuleId) - 1
            const removedModuleIds = this.stack.splice(-itemsToRemove, itemsToRemove)
            this.removeModules(removedModuleIds)
        },
        removeAllModules() {
            const removedModuleIds = this.stack.splice(0, this.stack.length)
            this.removeModules(removedModuleIds)
        },
        reset() {
            // cancel any current requests
            if (controller) {
                controller.abort()
            }

            this.loadingId = null
            this.stack = []
            this.modules = {}
        },
        async markAsRead(url, id) {
            // Disable marking as read when mocking
            if (mock.mocking()) {
                return
            }

            // Capture the most recent url as it may change by the time we get a response
            const mostRecentUrl = this.mostRecentUrl

            return api.post(url)
                .then(() => {
                    if (id) {
                        events.emit('read', id)
                        this.addReadItemId(id, mostRecentUrl)
                        console.log('read', url, id)
                    }
                })
        },
        async markMultipleAsRead(url, ids) {
            if (!ids.length) {
                return
            }
            // Capture the most recent url as it may change by the time we get a response
            const mostRecentUrl = this.mostRecentUrl

            return api.post(url, { ids })
                .then(() => {
                    each(ids, (id) => {
                        events.emit('read', id)
                        this.addReadItemId(id, mostRecentUrl)
                    })
                })
        },
        addReadItemId(id, moduleUrl) {
            if (!this.readItemIdsByModuleUrl[moduleUrl]) {
                this.readItemIdsByModuleUrl[moduleUrl] = []
            }
            this.readItemIdsByModuleUrl[moduleUrl].push(id)

            const badgeStore = useBadgeStore()
            badgeStore.decrementCount(moduleUrl)
        },
        setQueryParams(moduleId, params) {
            set(this.modules, [moduleId, 'params'], params)
        },
    },
})
